<!-- One card, as Market vacancy detail: title row with back, subject and actions; mail left, details right. The verdict heading in the details names the verdict. -->
<nb-card class="mail-card">
  <nb-card-header>
    <div class="title-row">
      <a nbButton status="basic" size="small" class="back" routerLink="/market/intake">
        <nb-icon icon="arrow-back-outline"></nb-icon>{{ 'intake.mail.back' | translate }}
      </a>
      @if (mail) {
        <div class="title">
          <h1>
            @if (contentGone && !mail.subject) {
              <em>{{ 'intake.list.content_removed' | translate }}</em>
            } @else {
              {{ mail.subject || ('intake.list.no_subject' | translate) }}
            }
          </h1>
        </div>
      }
      <div class="header-actions">
        @if (mail && canDelete && !contentGone) {
          <button nbButton size="small" status="danger" ghost class="delete-wide" [disabled]="deleting" (click)="deleteMail()">
            <nb-icon icon="trash-2-outline"></nb-icon>{{ 'intake.mail.delete' | translate }}
          </button>
          <!-- Phone: the label collided with the header avatar, so the action moves into the overflow menu. -->
          <button nbButton ghost size="small" class="delete-narrow"
                  [nbPopover]="moreTpl" nbPopoverTrigger="click" nbPopoverPlacement="bottom"
                  [attr.aria-label]="'intake.mail.more' | translate">
            <nb-icon icon="more-vertical-outline"></nb-icon>
          </button>
          <ng-template #moreTpl>
            <div class="kebab-popup">
              <button class="kebab-item kebab-item--danger" [disabled]="deleting" (click)="deleteMail()">
                <nb-icon icon="trash-2-outline"></nb-icon>{{ 'intake.mail.delete' | translate }}
              </button>
            </div>
          </ng-template>
        }
      </div>
    </div>
  </nb-card-header>

  @if (notFound || mail) {
    <nb-card-body>
      @if (notFound) {
        <p class="empty">{{ 'intake.mail.not_found' | translate }}</p>
      }
      @if (mail) {
        <div class="mail-layout">
          <section class="mail-col">
            <ul class="meta">
              @if (mail.sender) {
                <li>{{ mail.sender }}</li>
              }
              <li [attr.title]="metaInfo || null">{{ mail.received_at | intakeDate: 'dayTime' }}</li>
              @if (mail.vacancy && mail.verdict !== 'direct_request') {
                <li>{{ 'intake.mail.matches' | translate }}
                  <a [routerLink]="['/market/vacancies', mail.vacancy.id]">{{ mail.vacancy.title || ('intake.mail.open_request' | translate) }}</a>
                </li>
              }
            </ul>

            @if (frameUrl) {
              <h3 class="intake-head" [title]="'intake.mail.frame_note' | translate">{{ 'intake.mail.frame_label' | translate }}</h3>
              @if (frameDoc) {
                <iframe class="mail-frame" sandbox="" referrerpolicy="no-referrer" [srcdoc]="frameDoc" [title]="'intake.mail.frame_label' | translate"></iframe>
              } @else if (frameFallback) {
                <iframe class="mail-frame" sandbox="" referrerpolicy="no-referrer" [src]="frameUrl" [title]="'intake.mail.frame_label' | translate"></iframe>
              }
            } @else if (bodyText) {
              <h3 class="intake-head">{{ 'intake.mail.text_label' | translate }}</h3>
              <div class="mail-frame mail-text">{{ bodyText }}</div>
            } @else if (contentGone) {
              <p class="gone">{{ 'intake.mail.content_gone' | translate }}</p>
            }

            @if (attachments.length) {
              <section class="attachments">
                <h3 class="intake-head">{{ 'intake.mail.attachments' | translate }}</h3>
                <ul>
                  @for (a of attachments; track a.id) {
                    <li><a [href]="a.href" download><nb-icon icon="paperclip" pack="lucide"></nb-icon>{{ a.display_name }}</a> <span class="hint">{{ a.size / 1024 | number: '1.0-0' }} KB</span></li>
                  }
                </ul>
              </section>
            }
          </section>

          <aside class="side-col">
            <bst-intake-verdict-panel [mail]="mail" (mailChange)="setMail($event)"></bst-intake-verdict-panel>

            @if (contacts.length) {
              <section class="contacts">
                <h3 class="intake-head" [title]="contactsHint">{{ 'intake.mail.contacts' | translate: { count: contacts.length } }}</h3>
                @for (contact of contacts; track $index) {
                  <div class="contact detail-rows">
                    @if (contact.name) {
                      <div class="detail-row">
                        <span class="detail-label">{{ 'intake.mail.contact_name' | translate }}</span>
                        <span class="detail-value copy-value"><span>{{ contact.name }}</span><bst-intake-copy [text]="contact.name" label="intake.mail.copy_name"></bst-intake-copy></span>
                      </div>
                    }
                    @if (contact.email) {
                      <div class="detail-row">
                        <span class="detail-label">{{ 'intake.mail.contact_email' | translate }}</span>
                        <span class="detail-value copy-value"><span>{{ contact.email }}</span><bst-intake-copy [text]="contact.email" label="intake.mail.copy_email"></bst-intake-copy></span>
                      </div>
                    }
                    @if (contact.phone) {
                      <div class="detail-row">
                        <span class="detail-label">{{ 'intake.mail.contact_phone' | translate }}</span>
                        <span class="detail-value copy-value"><span>{{ contact.phone }}</span><bst-intake-copy [text]="contact.phone" label="intake.mail.copy_phone"></bst-intake-copy></span>
                      </div>
                    }
                  </div>
                }
              </section>
            }

            <!-- History stays closed: one quiet toggle at the bottom; the body opens on grid rows, never on height. -->
            @if (logCount) {
              <section class="history" [class.open]="historyOpen">
                <button type="button" class="fold-head" [attr.aria-expanded]="historyOpen" aria-controls="intake-history" (click)="toggleHistory()">
                  <nb-icon icon="chevron-right-outline" class="chevron"></nb-icon>
                  <span>{{ 'intake.mail.log' | translate: { count: logCount } }}</span>
                </button>
                <div class="fold-body" id="intake-history" role="region" [attr.aria-label]="'intake.mail.log' | translate: { count: logCount }">
                  <div class="fold-inner">
                    <ul class="log-entries">
                      @for (e of mail.events; track e.id) {
                        <li [attr.title]="logTip(e)">
                          <div class="log-meta">
                            {{ e.created_at | intakeDate: 'dayTime' }}
                            @if (e.actor_name) { · {{ e.actor_name }} }
                          </div>
                          <div class="log-body">
                            {{ 'intake.event.' + e.event | translate }}
                            @if (e.verdict) { · {{ 'intake.verdict.' + e.verdict | translate }} }
                            @if (e.held_reason) { · {{ 'intake.held.' + e.held_reason | translate }} }
                            @if (e.confidence !== null) { · {{ 'intake.mail.log_confidence' | translate: { value: (e.confidence | number: '1.2-2') } }} }
                            @if (e.outcome && e.outcome !== 'ok') { · {{ 'intake.outcome.' + e.outcome | translate }} }
                          </div>
                        </li>
                      }
                    </ul>
                  </div>
                </div>
              </section>
            }
          </aside>
        </div>
      }
    </nb-card-body>
  }
</nb-card>
